/**
 * Step 8 Mobile: Assumptions
 * Economic assumptions as sliders with % display.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Step7FormData, Step7Schema, type AllStepFormData } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';

interface Step6Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step7FormData) => void;
  onSave: (data: Step7FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

const sliders: { name: keyof Step7FormData['economicAssumptions']; label: string; min: number; max: number; step: number; suffix: string }[] = [
  { name: 'equityReturnAnnualPct', label: 'Equity Return', min: 0, max: 15, step: 0.5, suffix: '%' },
  { name: 'bondReturnAnnualPct', label: 'Bond Return', min: 0, max: 10, step: 0.5, suffix: '%' },
  { name: 'inflationAnnualPct', label: 'Inflation', min: 0, max: 10, step: 0.25, suffix: '%' },
  { name: 'taxBandInflationPct', label: 'Tax Band Inflation', min: 0, max: 5, step: 0.25, suffix: '%' },
  { name: 'statePensionGrowthAddPct', label: 'State Pension Growth', min: 0, max: 3, step: 0.25, suffix: '%' },
  { name: 'dividendYieldPct', label: 'Dividend Yield', min: 0, max: 8, step: 0.25, suffix: '%' },
];

export default function Step6AssumptionsMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step6Props>) {
  const { register, handleSubmit, watch, setValue } = useForm<Step7FormData>({
    resolver: zodResolver(Step7Schema),
    defaultValues: {
      householdCash: { jointAccountBalance: initialData?.householdCash?.jointAccountBalance ?? 0 },
      economicAssumptions: {
        equityReturnAnnualPct: initialData?.economicAssumptions?.equityReturnAnnualPct ?? 6.5,
        bondReturnAnnualPct: initialData?.economicAssumptions?.bondReturnAnnualPct ?? 3.5,
        inflationAnnualPct: initialData?.economicAssumptions?.inflationAnnualPct ?? 2.25,
        taxBandInflationPct: initialData?.economicAssumptions?.taxBandInflationPct ?? 0,
        statePensionGrowthAddPct: initialData?.economicAssumptions?.statePensionGrowthAddPct ?? 0.5,
        dividendYieldPct: initialData?.economicAssumptions?.dividendYieldPct ?? 2,
      },
    },
  });

  const watched = watch('economicAssumptions');

  const handleBlur = () => onSave(watch());

  return (
    <WizardMobileShell step={8} totalSteps={10} title="Assumptions" onBack={onBack} onContinue={() => handleSubmit(onNext)()} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <label htmlFor="m-cash" className="mobile-label">Household Cash (£)</label>
        <input id="m-cash" type="number" className="mobile-input" {...register('householdCash.jointAccountBalance', { valueAsNumber: true })} onBlur={handleBlur} />

        {sliders.map(s => (
          <div key={s.name}>
            <label htmlFor={`m-slider-${s.name}`} className="mobile-label">{s.label}: {watched[s.name]}{s.suffix}</label>
            <input id={`m-slider-${s.name}`} type="range" className="mobile-slider" min={s.min} max={s.max} step={s.step}
              value={watched[s.name] as number} aria-label={`${s.label}: ${watched[s.name]}${s.suffix}`}
              onChange={e => { setValue(`economicAssumptions.${s.name}`, parseFloat(e.target.value)); handleBlur(); }}
            />
          </div>
        ))}

        <button type="button" className="mobile-btn mobile-btn--outline mobile-btn--full" onClick={() => {
          setValue('economicAssumptions.equityReturnAnnualPct', 6.5);
          setValue('economicAssumptions.bondReturnAnnualPct', 3.5);
          setValue('economicAssumptions.inflationAnnualPct', 2.25);
          setValue('economicAssumptions.taxBandInflationPct', 0);
          setValue('economicAssumptions.statePensionGrowthAddPct', 0.5);
          setValue('economicAssumptions.dividendYieldPct', 2);
          handleBlur();
        }}>Reset to defaults</button>
      </div>
    </WizardMobileShell>
  );
}
